<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-key"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Assign Permissions</div>
          <div class="panel-header__sub">
            Configure access rights for role:
            <strong>{{ role.title || '…' }}</strong>
          </div>
        </div>
        <div class="panel-header__action">
          <button class="btn-back" @click="goBack">
            <i class="fa fa-arrow-left"></i> Back to Roles
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <div class="state-spinner"></div>
        <span>Loading permissions…</span>
      </div>

      <!-- Permissions -->
      <div class="perm-wrap" v-else>

        <!-- Select all bar -->
        <div class="select-all-bar">
          <label class="select-all-check">
            <input type="checkbox" v-model="selectAll" @change="selectAllPermissions" />
            <span class="check-box" :class="{ checked: selectAll }">
              <i class="fa fa-check" v-if="selectAll"></i>
            </span>
            <span class="select-all-label">Select All Permissions</span>
          </label>
          <span class="perm-count">
            {{ checkedCount }} / {{ permissions_data.length }} selected
          </span>
        </div>

        <!-- Permission cards -->
        <div class="perm-grid">
          <label
            v-for="(option, index) in permissions_data"
            :key="index"
            class="perm-card"
            :class="{ 'perm-card--active': option.checked }"
          >
            <input type="checkbox" v-model="option.checked" @change="syncSelectAll" />
            <div class="perm-card__icon">
              <i :class="permIcon(option.title)"></i>
            </div>
            <div class="perm-card__text">
              <div class="perm-card__title">{{ option.title }}</div>
              <div class="perm-card__sub">{{ permDesc(option.title) }}</div>
            </div>
            <div class="perm-card__check" :class="{ active: option.checked }">
              <i class="fa fa-check" v-if="option.checked"></i>
            </div>
          </label>
        </div>

      </div>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar" v-if="!loadingData">
      <span class="save-bar__hint">
        <i class="fa fa-info-circle"></i>
        {{ checkedCount }} permission{{ checkedCount !== 1 ? 's' : '' }} selected
      </span>
      <button class="save-bar__save" @click="update">
        <i class="fa fa-save"></i> Update Permissions
      </button>
    </div>
  </div>
</template>

<script>
import {
  FETCH_ROLE_PERMISSIONS,
  CREATE_ROLE_PERMISSIONS,
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";

export default {
  data() {
    return {
      loadingData: false,
      role: "",
      selectAll: false,
      permissions_data: [
        { title: "Export", checked: false },
        { title: "Login",  checked: false },
        { title: "Print",  checked: false },
      ],
      breadCrumbs: [
        { id: 1, title: "Dashboard",   route: "client.dashboard" },
        { id: 2, title: "Role List",   route: "client.roles" },
        { id: 3, title: "Edit Permissions", route: "" },
      ],
    };
  },
  computed: {
    checkedCount() {
      return this.permissions_data.filter(p => p.checked).length;
    },
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getPermissionData();
  },
  methods: {
    permIcon(title) {
      const map = {
        Export: "fa fa-download",
        Login:  "fa fa-sign-in",
        Print:  "fa fa-print",
      };
      return map[title] || "fa fa-check-circle";
    },
    permDesc(title) {
      const map = {
        Export: "Allow exporting data to files",
        Login:  "Allow logging into the system",
        Print:  "Allow printing records and reports",
      };
      return map[title] || "";
    },
    selectAllPermissions() {
      this.permissions_data.forEach(p => { p.checked = this.selectAll; });
    },
    syncSelectAll() {
      this.selectAll = this.permissions_data.every(p => p.checked);
    },
    goBack() {
      this.$router.push({ name: "client.roles" });
    },
    getPermissionData() {
      this.loadingData = true;
      this.$store
        .dispatch(FETCH_ROLE_PERMISSIONS, this.$route.params.id)
        .then(perm_data => {
          perm_data.data.forEach(permission => {
            const key = this.permissions_data.findIndex(x => x.title === permission.title);
            if (key >= 0) this.permissions_data[key].checked = true;
          });
          this.syncSelectAll();
          this.loadingData = false;
        })
        .catch(() => { this.loadingData = false; });
    },
    update() {
      const selected = this.permissions_data.filter(p => p.checked);
      if (selected.length === 0) {
        this.$toastr.e("Please select at least one permission");
        return;
      }
      this.$store
        .dispatch(CREATE_ROLE_PERMISSIONS, {
          role_id: this.$route.params.id,
          permissions: selected,
        })
        .then(data => {
          this.$toastr.s(data.msg);
          this.$router.push({ name: "client.roles" });
        })
        .catch(err => {
          this.errors = err;
          this.selectAll = false;
          this.getPermissionData();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

@keyframes spin { to { transform: rotate(360deg); } }

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px; flex-shrink: 0;
  }
  &__text  { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   {
    font-size: 12px; color: #7a8499; margin-top: 3px;
    strong { color: $dark; font-weight: 600; }
  }
  &__action { display: flex; align-items: center; }
}

.btn-back {
  display: inline-flex; align-items: center; gap: 6px;
  height: 34px; padding: 0 14px; border-radius: 8px;
  border: 1.5px solid $border; background: #fff;
  color: $dark; font-size: 13px; font-weight: 600;
  font-family: $font; cursor: pointer; white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;

  i { font-size: 11px; color: #7a8499; }
  &:hover { background: #f4f6fb; border-color: #c5ccd8; }
}

/* ── state ── */
.state-box {
  display: flex; flex-direction: column; align-items: center;
  padding: 56px 24px; gap: 8px; color: #7a8499; font-size: 13px;
}

.state-spinner {
  width: 32px; height: 32px;
  border: 3px solid #c5d8f5; border-top-color: $blue;
  border-radius: 50%; animation: spin 0.7s linear infinite; margin-bottom: 8px;
}

/* ── perm wrap ── */
.perm-wrap {
  padding: 24px 28px 28px;
}

/* ── select all bar ── */
.select-all-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8faff;
  border: 1.5px solid $border;
  border-radius: 10px;
  margin-bottom: 20px;
}

.select-all-check {
  display: inline-flex; align-items: center; gap: 10px; cursor: pointer;

  input[type="checkbox"] { display: none; }
}

.check-box {
  width: 20px; height: 20px; border-radius: 5px;
  border: 2px solid $border; background: #fff;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, border-color 0.15s; flex-shrink: 0;

  i { font-size: 10px; color: #fff; }

  &.checked { background: $blue; border-color: $blue; }
}

.select-all-label {
  font-size: 13px; font-weight: 600; color: $dark;
}

.perm-count {
  font-size: 12px; color: #7a8499; font-weight: 500;
}

/* ── permission cards ── */
.perm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.perm-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1.5px solid $border;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  background: #fff;

  input[type="checkbox"] { display: none; }

  &:hover { border-color: rgba(56,154,212,0.4); background: #f8fbff; }

  &--active {
    border-color: $blue;
    background: rgba(56,154,212,0.04);
    box-shadow: 0 0 0 3px rgba(56,154,212,0.08);
  }

  &__icon {
    width: 38px; height: 38px; border-radius: 9px;
    background: rgba(56,154,212,0.1); color: $blue;
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; flex-shrink: 0;
    transition: background 0.15s;

    .perm-card--active & {
      background: $blue; color: #fff;
    }
  }

  &__text { flex: 1; min-width: 0; }

  &__title {
    font-size: 13px; font-weight: 600; color: $dark; line-height: 1.2;
  }

  &__sub {
    font-size: 11px; color: #7a8499; margin-top: 2px; line-height: 1.3;
  }

  &__check {
    width: 20px; height: 20px; border-radius: 50%;
    border: 2px solid $border; background: #fff;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, border-color 0.15s; flex-shrink: 0;

    i { font-size: 9px; color: #fff; }

    &.active { background: $blue; border-color: $blue; }
  }
}

/* ── save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,27,62,0.3);
  z-index: 100;

  &__hint {
    display: flex; align-items: center; gap: 7px;
    font-size: 12px; color: rgba(255,255,255,0.55); font-family: $font;
    i { font-size: 12px; }
  }

  &__save {
    display: inline-flex; align-items: center; gap: 7px;
    background: $blue; color: #fff; border: none; border-radius: 9px;
    padding: 9px 22px; font-size: 13px; font-weight: 600;
    font-family: $font; cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover { background: #2d85bb; box-shadow: 0 4px 14px rgba(56,154,212,0.4); }
    i { font-size: 12px; }
  }
}
</style>
